import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Immutable from "immutable";
import { useStore } from "stores/connect";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import { getConfig } from "components/configurations/helpers";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { Button, Row, Col, BootstrapModalForm, Input } from "components/bootstrap";
import { IfPermitted, ISODurationInput } from "components/common";
import Spinner from "components/common/Spinner";
import Select from "components/common/Select/Select";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import "moment-duration-format";
import TimeRangePresetForm from "components/configurations/TimeRangePresetForm";
import generateId from "logic/generateId";
import TimeRangePresetOptionsSummary from "components/configurations/TimeRangePresetOptionSummary";
import { onInitializingTimerange } from "views/components/TimerangeForForm";
import useUserDateTime from "hooks/useUserDateTime";
import { normalizeFromSearchBarForBackend } from "views/logic/queries/NormalizeTimeRange";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useMinimumRefreshInterval from "views/hooks/useMinimumRefreshInterval";
import Alert from "components/bootstrap/Alert";
import ReadableDuration from "components/common/ReadableDuration";
import TimeRangeOptionsForm from "./TimeRangeOptionsForm";
import TimeRangeOptionsSummary from "./TimeRangeOptionsSummary";
const queryTimeRangeLimitValidator = (milliseconds) => milliseconds >= 1;
const surroundingTimeRangeValidator = (milliseconds) => milliseconds >= 1;
const autoRefreshTimeRangeValidator = (minimumRefreshIntervalMS) => (milliseconds) => milliseconds >= 1e3 && milliseconds >= minimumRefreshIntervalMS;
const splitStringList = (stringList) => stringList.split(",").map((f) => f.trim()).filter((f) => f.length > 0);
const buildTimeRangeOptions = (options) => Object.keys(options).map((key) => ({ period: key, description: options[key] }));
const mapQuickAccessBEData = (items, formatTime) => Immutable.List(
  items.map(({ timerange, description, id }) => {
    const presetId = id != null ? id : generateId();
    return { description, id: presetId, timerange: onInitializingTimerange(timerange, formatTime) };
  })
);
const SearchesConfig = () => {
  const { userTimezone, formatTime } = useUserDateTime();
  const isLimitEnabled = (config) => moment.duration(config == null ? void 0 : config.query_time_range_limit).asMilliseconds() > 0;
  const { data: minimumRefreshInterval, isInitialLoading: isLoadingMinimumRefreshInterval } = useMinimumRefreshInterval();
  const minimumRefreshIntervalMS = moment.duration(minimumRefreshInterval).asMilliseconds();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [viewConfig, setViewConfig] = useState(void 0);
  const [formConfig, setFormConfig] = useState(void 0);
  const configuration = useStore(ConfigurationsStore, (state) => state == null ? void 0 : state.configuration);
  const [relativeTimeRangeOptionsUpdate, setRelativeTimeRangeOptionsUpdate] = useState(
    void 0
  );
  const [surroundingTimeRangeOptionsUpdate, setSurroundingTimeRangeOptionsUpdate] = useState(
    void 0
  );
  const [autoRefreshTimeRangeOptionsUpdate, setAutoRefreshTimeRangeOptionsUpdate] = useState(
    void 0
  );
  const [surroundingFilterFieldsUpdate, setSurroundingFilterFieldsUpdate] = useState(void 0);
  const [analysisDisabledFieldsUpdate, setAnalysisDisabledFieldsUpdate] = useState(void 0);
  const [defaultAutoRefreshOptionUpdate, setDefaultAutoRefreshOptionUpdate] = useState(void 0);
  const [timeRangePresetsUpdated, setTimeRangePresetsUpdated] = useState(void 0);
  const [showCancelAfterSeconds, setShowCancelAfterSeconds] = useState(false);
  const sendTelemetry = useSendTelemetry("search");
  useEffect(() => {
    ConfigurationsActions.list(ConfigurationType.SEARCHES_CLUSTER_CONFIG).then(() => {
      const config = getConfig(ConfigurationType.SEARCHES_CLUSTER_CONFIG, configuration);
      setViewConfig(config);
      setFormConfig(config);
      setShowCancelAfterSeconds(!!(config == null ? void 0 : config.cancel_after_seconds));
    });
  }, [configuration]);
  const onUpdate = (field) => (newOptions) => {
    setFormConfig({ ...formConfig, [field]: newOptions });
  };
  const onTimeRangePresetsUpdate = (data) => {
    setTimeRangePresetsUpdated(data);
  };
  const onSurroundingTimeRangeOptionsUpdate = (data) => {
    setSurroundingTimeRangeOptionsUpdate(data);
  };
  const onAutoRefreshTimeRangeOptionsUpdate = (data) => {
    setAutoRefreshTimeRangeOptionsUpdate(data);
  };
  const onFilterFieldsUpdate = (e) => {
    setSurroundingFilterFieldsUpdate(e.target.value);
  };
  const onAnalysisDisabledFieldsUpdate = (e) => {
    setAnalysisDisabledFieldsUpdate(e.target.value);
  };
  const onAutoRefreshDefaultOptionsUpdate = (data) => {
    setDefaultAutoRefreshOptionUpdate(data);
  };
  const onChecked = () => {
    let queryTimeRangeLimit;
    if (isLimitEnabled(formConfig)) {
      queryTimeRangeLimit = "PT0S";
    } else {
      queryTimeRangeLimit = "P30D";
    }
    setFormConfig({ ...formConfig, query_time_range_limit: queryTimeRangeLimit });
  };
  const onCancelAfterSecondsChanged = ({ target: { value } }) => {
    setFormConfig({ ...formConfig, cancel_after_seconds: Number(value) });
  };
  const onCheckedCancelAfterSeconds = () => {
    let cancelAfterSeconds;
    if (showCancelAfterSeconds) {
      cancelAfterSeconds = null;
    } else {
      cancelAfterSeconds = 30;
    }
    setShowCancelAfterSeconds((cur) => !cur);
    setFormConfig({ ...formConfig, cancel_after_seconds: cancelAfterSeconds });
  };
  const openModal = () => {
    setShowConfigModal(true);
  };
  const resetFormUpdates = () => {
    setRelativeTimeRangeOptionsUpdate(void 0);
    setSurroundingTimeRangeOptionsUpdate(void 0);
    setSurroundingFilterFieldsUpdate(void 0);
    setAnalysisDisabledFieldsUpdate(void 0);
    setAutoRefreshTimeRangeOptionsUpdate(void 0);
    setDefaultAutoRefreshOptionUpdate(void 0);
    setTimeRangePresetsUpdated(void 0);
  };
  const handleModalCancel = () => {
    setShowConfigModal(false);
    setFormConfig(viewConfig);
    resetFormUpdates();
  };
  const saveConfig = () => {
    var _a, _b;
    const update = { ...formConfig };
    sendTelemetry(TELEMETRY_EVENT_TYPE.CONFIGURATIONS.SEARCHES_UPDATED, {
      app_action_value: "configuration-save"
    });
    if (relativeTimeRangeOptionsUpdate) {
      update.relative_timerange_options = {};
      relativeTimeRangeOptionsUpdate.forEach((entry) => {
        update.relative_timerange_options[entry.period] = entry.description;
      });
      setRelativeTimeRangeOptionsUpdate(void 0);
    }
    if (timeRangePresetsUpdated) {
      update.quick_access_timerange_presets = timeRangePresetsUpdated.toArray().map(({ description, timerange, id }) => ({
        description,
        timerange: normalizeFromSearchBarForBackend(timerange, userTimezone),
        id
      }));
      setTimeRangePresetsUpdated(void 0);
    }
    if (surroundingTimeRangeOptionsUpdate) {
      update.surrounding_timerange_options = {};
      surroundingTimeRangeOptionsUpdate.forEach((entry) => {
        update.surrounding_timerange_options[entry.period] = entry.description;
      });
      setSurroundingTimeRangeOptionsUpdate(void 0);
    }
    if (surroundingFilterFieldsUpdate) {
      update.surrounding_filter_fields = splitStringList(surroundingFilterFieldsUpdate);
      setSurroundingFilterFieldsUpdate(void 0);
    }
    if (analysisDisabledFieldsUpdate) {
      update.analysis_disabled_fields = splitStringList(analysisDisabledFieldsUpdate);
      setAnalysisDisabledFieldsUpdate(void 0);
    }
    if (autoRefreshTimeRangeOptionsUpdate) {
      update.auto_refresh_timerange_options = Object.fromEntries(
        autoRefreshTimeRangeOptionsUpdate.map((entry) => [entry.period, entry.description])
      );
      setAutoRefreshTimeRangeOptionsUpdate(void 0);
    }
    const defaultAutoRefreshOption2 = defaultAutoRefreshOptionUpdate ? (_a = update.auto_refresh_timerange_options[defaultAutoRefreshOptionUpdate]) != null ? _a : Object.keys(update.auto_refresh_timerange_options)[0] : (_b = update.auto_refresh_timerange_options[update.default_auto_refresh_option]) != null ? _b : Object.keys(update.auto_refresh_timerange_options)[0];
    if (update.default_auto_refresh_option !== defaultAutoRefreshOption2) {
      update.default_auto_refresh_option = defaultAutoRefreshOptionUpdate;
      setDefaultAutoRefreshOptionUpdate(void 0);
    }
    const newFormConfig = { ...formConfig, ...update };
    ConfigurationsActions.update(ConfigurationType.SEARCHES_CLUSTER_CONFIG, newFormConfig).then(() => {
      setShowConfigModal(false);
      resetFormUpdates();
    });
  };
  const timeRangePresetsFromBE = useMemo(
    () => {
      var _a;
      return mapQuickAccessBEData((_a = formConfig == null ? void 0 : formConfig.quick_access_timerange_presets) != null ? _a : [], formatTime);
    },
    [formConfig == null ? void 0 : formConfig.quick_access_timerange_presets, formatTime]
  );
  const timeRangePresets = useMemo(
    () => timeRangePresetsUpdated != null ? timeRangePresetsUpdated : timeRangePresetsFromBE,
    [timeRangePresetsFromBE, timeRangePresetsUpdated]
  );
  if (!viewConfig) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const duration = (config) => moment.duration(config.query_time_range_limit);
  const limit = (config) => isLimitEnabled(config) ? `${config.query_time_range_limit} (${duration(config).humanize()})` : "disabled";
  const autoRefreshOptions = (config) => autoRefreshTimeRangeOptionsUpdate != null ? autoRefreshTimeRangeOptionsUpdate : buildTimeRangeOptions(config.auto_refresh_timerange_options);
  const formDefaultAutoRefreshOptionUpdate = (config) => defaultAutoRefreshOptionUpdate != null ? defaultAutoRefreshOptionUpdate : config.default_auto_refresh_option;
  const defaultAutoRefreshOption = (config) => {
    var _a;
    return autoRefreshOptions(config).find((option) => option.period === formDefaultAutoRefreshOptionUpdate(config)) ? formDefaultAutoRefreshOptionUpdate(config) : (_a = autoRefreshOptions[0]) == null ? void 0 : _a.period;
  };
  const cancellationTimeout = (config) => config.cancel_after_seconds ? `${config.cancel_after_seconds} seconds` : "disabled";
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, "Search Configuration"), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, "Query time range limit"), /* @__PURE__ */ React.createElement("dd", null, limit(viewConfig)), /* @__PURE__ */ React.createElement("dd", null, "The maximum time users can query data in the past. This prevents users from accidentally creating queries which span a lot of data and would need a long time and many resources to complete (if at all).")), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, "Cancellation timeout"), /* @__PURE__ */ React.createElement("dd", null, cancellationTimeout(viewConfig)), /* @__PURE__ */ React.createElement("dd", null, "The time in seconds per widget after which search execution will be canceled automatically. This minimizes the amount of executed searches and improves performance.")), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement("strong", null, "Search Time Range Presets"), /* @__PURE__ */ React.createElement(TimeRangePresetOptionsSummary, { options: timeRangePresetsFromBE.toArray() }), /* @__PURE__ */ React.createElement("strong", null, "Surrounding time range options"), /* @__PURE__ */ React.createElement(TimeRangeOptionsSummary, { options: viewConfig.surrounding_timerange_options })), /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement(Row, { style: { marginBottom: 20 } }, /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement("strong", null, "Surrounding search filter fields"), /* @__PURE__ */ React.createElement("ul", null, viewConfig.surrounding_filter_fields && viewConfig.surrounding_filter_fields.map((f) => /* @__PURE__ */ React.createElement("li", { key: f }, f))))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement("strong", null, "UI analysis disabled for fields"), /* @__PURE__ */ React.createElement("ul", null, viewConfig.analysis_disabled_fields && viewConfig.analysis_disabled_fields.map((f) => /* @__PURE__ */ React.createElement("li", { key: f }, f)))))), /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement("strong", null, "Auto-refresh interval options"), /* @__PURE__ */ React.createElement(TimeRangeOptionsSummary, { options: viewConfig.auto_refresh_timerange_options }), /* @__PURE__ */ React.createElement("strong", null, "Default auto-refresh interval"), /* @__PURE__ */ React.createElement(
    TimeRangeOptionsSummary,
    {
      options: {
        [viewConfig.default_auto_refresh_option]: viewConfig.auto_refresh_timerange_options[viewConfig.default_auto_refresh_option]
      }
    }
  ))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, "Edit configuration")), showConfigModal && formConfig && /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: true,
      bsSize: "large",
      title: "Update Search Configuration",
      onSubmitForm: saveConfig,
      onCancel: handleModalCancel,
      submitButtonText: "Update configuration"
    },
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement("label", { htmlFor: "query-limit-checkbox" }, "Query Time Range Limit"), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "query-limit-checkbox",
        type: "checkbox",
        label: "Enable query limit",
        name: "enabled",
        checked: isLimitEnabled(formConfig),
        onChange: onChecked
      }
    ), isLimitEnabled(formConfig) && /* @__PURE__ */ React.createElement(
      ISODurationInput,
      {
        id: "query-timerange-limit-field",
        duration: formConfig.query_time_range_limit,
        update: onUpdate("query_time_range_limit"),
        label: "Query time range limit (ISO8601 Duration)",
        help: 'The maximum time range for searches. (i.e. "P30D" for 30 days, "PT24H" for 24 hours)',
        validator: queryTimeRangeLimitValidator,
        required: true
      }
    ), /* @__PURE__ */ React.createElement("label", { htmlFor: "cancel_after_seconds_checkbox" }, "Query Cancellation Timeout"), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "cancel_after_seconds_checkbox",
        type: "checkbox",
        label: "Enable query cancellation timeout",
        name: "cancel_after_seconds_checkbox",
        checked: showCancelAfterSeconds,
        onChange: onCheckedCancelAfterSeconds,
        help: "The time in seconds per widget after which search execution will be canceled automatically. This minimizes the amount of executed searches and improves performance."
      }
    ), showCancelAfterSeconds && /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "cancel_after_seconds",
        type: "number",
        label: "Cancellation timeout",
        name: "cancel_after_seconds",
        min: "1",
        step: "1",
        pattern: "\\d+",
        required: true,
        value: formConfig.cancel_after_seconds,
        onChange: onCancelAfterSecondsChanged
      }
    ), /* @__PURE__ */ React.createElement(TimeRangePresetForm, { options: timeRangePresets, onUpdate: onTimeRangePresetsUpdate }), /* @__PURE__ */ React.createElement(
      TimeRangeOptionsForm,
      {
        options: surroundingTimeRangeOptionsUpdate || buildTimeRangeOptions(formConfig.surrounding_timerange_options),
        update: onSurroundingTimeRangeOptionsUpdate,
        validator: surroundingTimeRangeValidator,
        title: "Surrounding Timerange Options",
        help: /* @__PURE__ */ React.createElement("span", null, "Configure the available options for the ", /* @__PURE__ */ React.createElement("strong", null, "surrounding"), " time range selector as", " ", /* @__PURE__ */ React.createElement("strong", null, "ISO8601 duration"))
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "filter-fields-input",
        type: "text",
        label: "Surrounding search filter fields",
        onChange: onFilterFieldsUpdate,
        value: surroundingFilterFieldsUpdate || formConfig.surrounding_filter_fields && formConfig.surrounding_filter_fields.join(", "),
        help: "A ',' separated list of message fields that will be used as filter for the surrounding messages query.",
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "disabled-fields-input",
        type: "text",
        label: "Disabled analysis fields",
        onChange: onAnalysisDisabledFieldsUpdate,
        value: analysisDisabledFieldsUpdate || formConfig.analysis_disabled_fields && formConfig.analysis_disabled_fields.join(", "),
        help: "A ',' separated list of message fields for which analysis features like QuickValues will be disabled in the web UI.",
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      TimeRangeOptionsForm,
      {
        options: autoRefreshOptions(formConfig),
        update: onAutoRefreshTimeRangeOptionsUpdate,
        validator: autoRefreshTimeRangeValidator(minimumRefreshIntervalMS),
        title: "Auto-Refresh Interval Options",
        help: /* @__PURE__ */ React.createElement("span", null, "Configure the available options for the ", /* @__PURE__ */ React.createElement("strong", null, "auto-refresh"), " interval selector as", " ", /* @__PURE__ */ React.createElement("strong", null, "ISO8601 duration"))
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        label: "Default Auto-Refresh Option",
        id: "default-auto-refresh-option",
        required: true,
        help: "Select the interval which is used when auto-refresh is started without explicitly selecting one"
      },
      /* @__PURE__ */ React.createElement(
        Select,
        {
          placeholder: "Select the default interval",
          clearable: false,
          options: autoRefreshOptions(formConfig),
          displayKey: "description",
          valueKey: "period",
          onChange: onAutoRefreshDefaultOptionsUpdate,
          value: defaultAutoRefreshOption(formConfig)
        }
      )
    ), !isLoadingMinimumRefreshInterval && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, "Please note, a minimum refresh interval of ", /* @__PURE__ */ React.createElement(ReadableDuration, { duration: minimumRefreshInterval }), " (", minimumRefreshInterval, ") has been configured in the server configuration file. Only intervals which are equal or above the minimum can be used."))
  ));
};
export default SearchesConfig;
